import React from 'react';
import { Box, Typography, Switch } from '@mui/material';
import { GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './CategoryGames.module.scss';

export interface Game {
  id: string;
  title: string;
  provider: string;
  image?: string;
  inCategory?: boolean;
  isEnabled?: boolean;
}

interface GameCardProps {
  game: Game;
  dndId?: string;
  isSortable?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  onRemove?: (id: string) => void;
  isMultiSelectMode?: boolean;
  isDisabled?: boolean;
  showEnableToggle?: boolean;
  onToggleEnable?: (id: string, checked: boolean) => void;
}

const GameCard: React.FC<GameCardProps> = ({
  game,
  dndId,
  isSortable = false,
  isSelected = false,
  onSelect,
  onRemove,
  isMultiSelectMode = false,
  isDisabled = false,
  showEnableToggle = false,
  onToggleEnable,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dndId || game.id,
    disabled: !isSortable || isDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isDisabled ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    pointerEvents: (isDisabled ? 'none' : 'auto') as React.CSSProperties['pointerEvents'],
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`${styles.gameCard} ${isSelected ? styles.selected : ''} ${
        isDragging ? styles.dragging : ''
      } ${isDisabled ? styles.disabled : ''}`}
      onClick={() => !isDisabled && onSelect?.(game.id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        {isSortable && (
          <Box {...attributes} {...listeners} className={styles.dragHandle}>
            <GripVertical size={18} />
          </Box>
        )}

        {isMultiSelectMode && (
          <Switch
            checked={isSelected}
            size="small"
            sx={{
              p: 0.5,
              '&.Mui-checked': { color: 'var(--accent-blue)' },
              '&.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent-blue)' },
            }}
          />
        )}

        {game.image ? (
          <img src={game.image} className={styles.gameImage} alt={game.title} />
        ) : (
          <Box className={styles.gameImagePlaceholder}>{game.title.charAt(0)}</Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography className={styles.gameTitle} noWrap>
            {game.title}
          </Typography>
          <Typography className={styles.gameProvider} noWrap>
            {game.provider}
          </Typography>
        </Box>

        {showEnableToggle && onToggleEnable && (
          <Switch
            checked={game.isEnabled ?? false}
            onChange={(e) => {
              e.stopPropagation();
              onToggleEnable(game.id, e.target.checked);
            }}
            size="small"
            sx={{
              mr: 1,
              '&.Mui-checked': { color: 'var(--accent-blue)' },
              '&.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent-blue)' },
            }}
          />
        )}

        {onRemove && (
          <Box
            className={`${styles.iconBtn} ${styles.delete}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(game.id);
            }}
          >
            <Trash2 size={16} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GameCard;
